import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { enqueueSnackbar } from "notistack";
import * as React from "react";

import { SyncCollectionAssetsInput } from "../../generated/gql/graphql";
import { CollectionAssetsContext } from "../../providers/collection-assets-provider";

import { useCollectionSyncModal } from "./hooks/useCollectionSyncModal";

type Props = {
  collectionId: string;
  onClose: () => void;
};

export default function CollectionSyncModal(props: Props): React.ReactElement {
  const { collectionId, onClose } = props;

  const { totalCount } = React.useContext(CollectionAssetsContext);

  const [collectionInputs, setCollectionInputs] =
    React.useState<SyncCollectionAssetsInput>({ collectionId });
  const [synched, setSynched] = React.useState<boolean>(false);

  const {
    brands,
    subsidiaries,
    loading,
    syncAssetsLoading,
    syncAssetsData,
    onSyncCollectionAssets,
  } = useCollectionSyncModal();

  const handleChange = (e: SelectChangeEvent): void => {
    setCollectionInputs({
      ...collectionInputs,
      [e.target.name]: e.target.value,
    });
  };

  async function handleSyncCollectionAssets(): Promise<void> {
    try {
      await onSyncCollectionAssets(collectionInputs);
      enqueueSnackbar("Successfully started collection", {
        variant: "success",
      });
      setSynched(true);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to start collection sync.", { variant: "error" });
    }
  }

  return (
    <Dialog
      sx={{
        "& .MuiPaper-root": {
          "border-radius": "15px",
        },
      }}
      open
      onClose={onClose}
      maxWidth="md"
    >
      <DialogTitle className="text-[#DD2127] text-[24px]">
        <SendOutlinedIcon className="-rotate-45 mr-4 mt-[-7px]" />
        Deliver Collection
      </DialogTitle>
      <DialogContent className="h-full flex min-w-[650px] max-h-[500px] !pt-5 pl-[50px]">
        {(loading || syncAssetsLoading) && (
          <Box className="flex w-full justify-center">
            <CircularProgress />
          </Box>
        )}
        {!loading && !syncAssetsLoading && !synched && (
          <div className="flex justify-between items-end w-full">
            <div className="flex flex-col">
              <FormControl className="w-[250px] mb-20" size="small">
                <InputLabel>Select Client</InputLabel>
                <Select
                  className="!rounded-2xl"
                  label="Select Client"
                  onChange={handleChange}
                  name="brandId"
                >
                  {brands.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className="w-[250px] mb-20" size="small">
                <InputLabel>Select Subsidiary</InputLabel>
                <Select
                  className="!rounded-2xl"
                  label="Select Subsidiary"
                  onChange={handleChange}
                  name="subsidiaryId"
                >
                  {subsidiaries.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* <FormControl className="w-[250px]" size="small">
              <InputLabel>Select Destination</InputLabel>
              <Select
                className="!rounded-2xl"
                label="Select Destination"
                // onChange={handleChange}
              >
                <MenuItem value="">Bynder</MenuItem>
              </Select>
            </FormControl> */}
            </div>
            {collectionInputs.brandId && (
              <div className="w-[170px] text-[14px]">
                Are you sure you want to sync {totalCount} Assets to:{" "}
                <span className="font-bold capitalize">
                  {collectionInputs.brandId}
                </span>
                ?
              </div>
            )}
          </div>
        )}
        {synched && (
          <div className="text-center text-[24px] w-full">
            {totalCount} Assets already synced to: <br />{" "}
            <span className="capitalize">{collectionInputs.brandId}</span>
          </div>
        )}
      </DialogContent>
      <DialogActions className="flex justify-between p-[40px] pl-[50px] pt-0">
        <Button
          className="rounded-2xl capitalize"
          onClick={onClose}
          startIcon={<CloseIcon />}
          variant="outlined"
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          hidden={synched}
          onClick={handleSyncCollectionAssets}
          disabled={
            !collectionInputs?.brandId ||
            !collectionInputs?.subsidiaryId ||
            syncAssetsLoading
          }
          className="rounded-2xl capitalize"
          endIcon={<ArrowForwardIcon />}
          variant="outlined"
        >
          Sync Assets
        </Button>
        {synched && (
          <Button
            onClick={onClose}
            className="rounded-2xl capitalize"
            endIcon={<ArrowForwardIcon />}
            variant="outlined"
          >
            Sync new Assets
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
